import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { accountDeletionApi, profileApi } from '@pukka/api';
import auth from '@pukka/auth';
import { assets } from '@pukka/common';
import { assertUtil } from '@pukka/common/utils';
import { ToastContainer, toast } from '@pukka/toast';
import { Button, Shimmer, Text } from '@pukka/ui-components';
import { LanguagePicker } from '@pukka/ui-composite';
import { useFlagWithFallback } from '@pukka/unleash';

import { AccountDeletionForm } from './AccountDeletionForm';

function AccountDeletionPage() {
  const [enableMultiLanguage] = useFlagWithFallback('backoffice-multi-language');
  const { t } = useTranslation();
  const { data: profileData, isLoading } = profileApi.useProfile();
  const isOwner = profileData?.is_owner;

  const [ticketNumber, setTicketNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToLegacy = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleSignOut = useCallback(() => {
    auth.logout();
  }, []);

  const handleSubmit = async (reason: string, notes: string) => {
    try {
      setIsSubmitting(true);

      const res = await accountDeletionApi.createAccountDeletionTicket({
        reason,
        notes,
      });

      setTicketNumber(res.ticket_number);

      auth.logout(undefined, true);
    } catch (error: unknown) {
      assertUtil.assertObjectIsTruthy<{
        response: {
          data: {
            message: string;
            code: string;
          };
        };
      }>(error);

      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative h-screen w-screen bg-neutral-200">
      <ToastContainer closeOnClick newestOnTop={false} hideProgressBar />
      <header className="flex h-14 w-full items-center justify-between bg-legacy-500 px-4">
        <div className="mx-auto flex w-[1000px] items-center justify-between ">
          <a href="/">
            <img alt="Moka logo" width={40} height={40} src={assets.logoMoka} />
          </a>
          <div className="flex items-center gap-6">
            {enableMultiLanguage && <LanguagePicker />}
            <a
              href="https://help.mokapos.com/s/?language=in"
              target="_blank"
              className="text-neutral-400 hover:text-white"
              rel="noreferrer"
            >
              {t('common.helpButton')}
            </a>
            <button type="button" onClick={handleSignOut} className="min-w-max text-neutral-400 hover:text-white">
              {t('common.signOutButton')}
            </button>
          </div>
        </div>
      </header>
      <div className="flex items-center justify-center bg-neutral-200 pt-10">
        <div className="flex max-w-144 flex-col gap-5 rounded-xl bg-white px-12 py-16 text-center">
          <Text variant="title-2" weight="bold">
            {t('accountDeletion.title')}
            {ticketNumber ? ` ${t('accountDeletion.sent')}` : ''}
          </Text>
          {isLoading && (
            <div className="flex flex-col items-center">
              <Shimmer size="md" className="w-[480px]" />
              <Shimmer size="md" className="w-96" />
              <Shimmer size="xxl" className="mt-5 w-full" />
            </div>
          )}
          {!isLoading && !isOwner && (
            <>
              <Text>{t('accountDeletion.pleaseSubmitRequest')}</Text>
              <Button onClick={goToLegacy}>{t('accountDeletion.closeButton')}</Button>
            </>
          )}
          {!isLoading && isOwner && !!ticketNumber && (
            <>
              <Text>
                <Trans>{t('accountDeletion.requestReceived', { ticketNumber })}</Trans>
              </Text>
              <Button onClick={handleSignOut}>{t('accountDeletion.closeButton')}</Button>
            </>
          )}

          {!isLoading && isOwner && !ticketNumber && <AccountDeletionForm isSubmitting={isSubmitting} onSubmit={handleSubmit} />}
        </div>
      </div>
    </div>
  );
}

export { AccountDeletionPage };
